/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { createPortal } from "react-dom";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useId, useOverlay } from "react-aria";
import { cssFns } from "@superweb/css";
import { useLocale } from "@superweb/intl";
import { useUiColors } from "../theme";
import { useTypo } from "../typo";
import { Button } from "../buttons/button";
import { icons } from "../icons";
import { useIsMobile } from "../mobile-context";
import { PopoverContextProvider } from "../popover";
import { useCustomScrollbar } from "../scrollbar/scrollbar";
import type { Style } from "@superweb/css";

export const SlideoverContext = createContext<
  | {
      container: MutableRefObject<HTMLDivElement | null>;
      closeCallbacksRef?: MutableRefObject<
        Map<string, { callback: () => void; container: HTMLElement | null }>
      >;
    }
  | undefined
>(undefined);
export const SlideoverContextProvider = SlideoverContext.Provider;

const isElementFocusable = (node: unknown): node is HTMLElement => {
  return (
    !!node &&
    node instanceof HTMLElement &&
    document.body.contains(node) &&
    typeof node.focus === "function"
  );
};

type FocusableElement = Element & { focus: () => void };

const focusableElementsSelector =
  [
    "input:not([disabled]):not([type=hidden])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "button:not([disabled])",
    "a[href]",
    "area[href]",
    "summary",
    "iframe",
    "object",
    "embed",
    "audio[controls]",
    "video[controls]",
    "[contenteditable]",
  ].join(":not([hidden]),") + ",[tabindex]:not([disabled]):not([hidden])";

const getNearestNodes = (
  origin: Node | null,
): {
  origin: FocusableElement | null;
  nextNode: FocusableElement | null;
  previousNode: FocusableElement | null;
} => {
  if (!origin) {
    return {
      origin: null,
      nextNode: null,
      previousNode: null,
    };
  }
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_ELEMENT,
    {
      acceptNode(node) {
        if ((node as Element).matches(focusableElementsSelector)) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      },
    },
  );
  walker.currentNode = origin;
  const previousNode = walker.previousNode() as FocusableElement;
  let nextNode = walker.nextNode() as FocusableElement;
  if (nextNode === origin) {
    nextNode = walker.nextNode() as FocusableElement;
  }
  return {
    origin: origin as FocusableElement,
    nextNode,
    previousNode,
  };
};

const getBorderRadius = ({
  isMobile,
  locale,
}: {
  isMobile: boolean;
  locale: Intl.Locale | undefined;
}): Style => {
  if (!isMobile) {
    return locale?.textInfo.direction === "ltr"
      ? {
          borderTopLeftRadius: "24px",
          borderBottomLeftRadius: "24px",
        }
      : {
          borderTopRightRadius: "24px",
          borderBottomRightRadius: "24px",
        };
  }
  return {};
};

/**
 * A dialog variant that does not block interaction with the main page content in desktop mode.
 *
 * Slideover does not have a backdrop, so the user can interact with the underlying content while the slideover is open.
 *
 * However, slideover closes when the content outside is interacted with, including pointer events and keyboard focus.
 */
export const Slideover = ({
  title,
  onClose,
  size = "s",
  render,
  ...slots
}: {
  /**
   * A callback function fired when a close action is triggered.
   *
   * If not provided, the close button will not be rendered.
   */
  onClose?: () => void;

  /**
   * Determines slideover width in desktop mode.
   * @defaultValue "s"
   */
  size?: "s" | "m" | "l" | "xl";
} & (
  | {
      /**
       * The visible header text for the panel. This prop is required.
       *
       * Also serves as an accessible name for the whole dialog.
       */
      title: string;

      /**
       * Raw content of slideover.
       *
       * This content is rendered directly without any scrollable container or footer support.
       * For scrollable content with footer support, use the `main` prop instead.
       *
       */
      content: ReactNode;

      render?: never;
    }
  | {
      /**
       * The visible header text for the panel. This prop is required.
       *
       * Also serves as an accessible name for the whole Slideover.
       */
      title: string;

      /**
       * The primary content of the slideover.
       *
       * This area will become scrollable if its content overflows the available space.
       */
      main: ReactNode;

      /**
       * A dedicated slot for footer content, such as action buttons.
       *
       * The footer is "sticky" and always remains visible at the bottom, it is not part of the scrollable area.
       */
      footer: ReactNode;

      render?: never;
    }
  | {
      title?: never;

      content?: never;
      /**
       * Function which returns content for the slideover.
       * Use this function, if you need to get a specific slideover content structure.
       * It is recommended to use the titleId to link to your title by id attribute,
       * in order to maintain accessibility
       *
       * @example
       * ```
       * <Slideover
       * render={(titleId) => <div id={titleId}>Title</div>}
       * />
       * ```
       */
      render: (titleId: string) => ReactNode;
    }
)) => {
  const locale = useLocale();
  const isMobile = useIsMobile();
  const typo = useTypo();
  const uiColors = useUiColors();
  const panelRef = useRef<HTMLDivElement>(null);
  const focusOriginRef = useRef<Node | null>(document.activeElement);
  const nestedContainerRef = useRef<HTMLDivElement | null>(null);
  const [popoverContainer, setPopoverContainer] =
    useState<HTMLDivElement | null>(null);
  const slideoverId = useId();
  const titleId = useId();
  const slideoverContext = useContext(SlideoverContext);
  if (!slideoverContext)
    throw new Error("Slideover must have an access to SlideoverContext");
  const { container: containerRef, closeCallbacksRef } = slideoverContext;

  const { overlayProps } = useOverlay(
    {
      onClose,
      isKeyboardDismissDisabled: !onClose,
      isDismissable: false,
      isOpen: true,
    },
    panelRef,
  );

  const maxWidth = (() => {
    if (isMobile) return "100%";
    switch (size) {
      case "s":
        return "360px";
      case "m":
        return "448px";
      case "l":
        return "528px";
      case "xl":
        return "764px";
      default:
        return "448px";
    }
  })();

  const maxHeight = "100%";
  const borderRadius = getBorderRadius({ isMobile, locale });

  const registerClose = useCallback(
    (id: string, callback: () => void, container: HTMLElement | null) => {
      closeCallbacksRef?.current.set(id, { callback, container });

      if (container && container === containerRef.current) {
        closeCallbacksRef?.current.forEach((entry, otherId) => {
          if (otherId === id) return;

          if (entry.container === container) {
            try {
              entry.callback();
            } catch {}
          }
        });
      }
    },
    [containerRef, closeCallbacksRef],
  );

  const unregisterClose = useCallback(
    (id: string) => {
      closeCallbacksRef?.current.delete(id);
    },
    [closeCallbacksRef],
  );

  const nestedContextValue = useMemo(
    () => ({
      container: nestedContainerRef,
      closeCallbacksRef,
    }),
    [closeCallbacksRef],
  );

  useLayoutEffect(() => {
    let rafId: number | null = null;
    let cancelled = false;

    const doRegister = () => {
      if (cancelled) return;

      const portalTarget = slideoverContext.container.current ?? null;
      registerClose(slideoverId, () => onClose?.(), portalTarget);
    };

    rafId = requestAnimationFrame(doRegister);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      unregisterClose(slideoverId);
    };
  }, [
    slideoverId,
    registerClose,
    unregisterClose,
    slideoverContext.container,
    onClose,
  ]);

  useEffect(() => {
    requestAnimationFrame(() => {
      panelRef.current?.focus();
    });

    if (!focusOriginRef.current) {
      focusOriginRef.current = document.activeElement as HTMLElement | null;
    }

    return () => {
      const el = focusOriginRef.current;
      if (isElementFocusable(el)) {
        el.focus({ preventScroll: true });
      } else {
        const { nextNode, previousNode } = getNearestNodes(el);
        if (isElementFocusable(nextNode)) {
          nextNode.focus();
        } else if (isElementFocusable(previousNode)) {
          previousNode.focus();
        }
      }
      focusOriginRef.current = null;
    };
  }, []);

  if (!slideoverContext.container.current) {
    return null;
  }

  return createPortal(
    <SlideoverContextProvider value={nestedContextValue}>
      <PopoverContextProvider value={{ container: popoverContainer }}>
        <div
          css={{
            color: uiColors.text,
            position: "relative",
          }}
        >
          <section
            role="presentation"
            aria-labelledby={titleId}
            css={{
              ...cssFns.placeContent("flex-end"),
              ...cssFns.inset("0"),
              position: "fixed",
              display: "flex",
              pointerEvents: "none",
            }}
          >
            <div
              ref={panelRef}
              tabIndex={0}
              role="region"
              aria-labelledby={titleId}
              {...overlayProps}
              css={{
                pointerEvents: "auto",
                outlineStyle: "none",
                backgroundColor: uiColors.background,
                maxWidth,
                minWidth: maxWidth,
                maxHeight,
                display: "grid",
                gridTemplateRows: title ? "max-content 1fr" : "1fr",
                ...borderRadius,
                ...cssFns.boxShadow({
                  offsetY: "8px",
                  blurRadius: "20px",
                  color: cssFns.setOpacity(uiColors.everBack, 0.12),
                }),
                ...cssFns.overflow("hidden"),
                isolation: "isolate",
              }}
            >
              {render ? (
                render(titleId)
              ) : (
                <>
                  <div
                    css={{
                      display: "grid",
                      alignItems: "flex-start",
                      minHeight: "60px",
                      boxSizing: "border-box",
                      paddingBlockStart: "8px",
                      paddingBlockEnd: "4px",
                      paddingInlineStart: "16px",
                      paddingInlineEnd: "8px",
                      gridTemplateColumns: "1fr auto",
                      columnGap: "8px",
                    }}
                  >
                    <div
                      id={titleId}
                      lang={locale.language}
                      css={{
                        display: "flex",
                        hyphens: "auto",
                        wordBreak: "break-word",
                        maxWidth: "100%",
                        paddingBlockStart: "10px",
                        ...typo({
                          level: "title4",
                          weight: "medium",
                          density: "normal",
                        }),
                        ...cssFns.margin("0px"),
                      }}
                    >
                      {title}
                    </div>
                    {onClose && (
                      <Button
                        variant="ghost"
                        icon={icons.CrossL}
                        onPress={onClose}
                        ariaLabel="Close"
                      />
                    )}
                  </div>
                  <div
                    css={{
                      display: "grid",
                      boxSizing: "border-box",
                      ...cssFns.overflow("hidden"),
                      ...(isMobile && {
                        ...cssFns.overflow("auto"),
                      }),
                    }}
                  >
                    {"main" in slots && "footer" in slots ? (
                      <SlideoverContent
                        main={slots.main}
                        footer={slots.footer}
                      />
                    ) : (
                      slots.content
                    )}
                  </div>
                </>
              )}
            </div>
          </section>
          <div ref={(element) => setPopoverContainer(element)} />
          <div ref={nestedContainerRef} />
        </div>
      </PopoverContextProvider>
      ,
    </SlideoverContextProvider>,
    slideoverContext.container.current,
  );
};

const SlideoverContent = ({
  main,
  footer,
}: {
  main: ReactNode;
  footer?: ReactNode;
}) => {
  const uiColors = useUiColors();
  const parentRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const customScrollbarCSS = useCustomScrollbar();
  const [isChildOverflow, setIsChildOverflow] = useState(false);

  useLayoutEffect(() => {
    if (!innerRef.current || !parentRef.current) return;
    const el = innerRef.current;
    const parent = parentRef.current;
    const observer = new ResizeObserver(() => {
      setIsChildOverflow(el.scrollHeight - 8 >= parent.clientHeight);
    });
    observer.observe(el);
    return () => {
      observer.unobserve(el);
    };
  }, [setIsChildOverflow]);

  return (
    <div
      css={{
        display: "grid",
        gridTemplateRows: "1fr max-content",
        ...cssFns.overflow("hidden"),
      }}
    >
      <div
        ref={parentRef}
        css={{
          ...cssFns.overflow("auto"),
        }}
        {...customScrollbarCSS}
      >
        <div
          ref={innerRef}
          css={{
            display: "grid",
            ...cssFns.padding("16px"),
            paddingBlockStart: "0px",
            ...(isChildOverflow && { paddingBlockEnd: "8px" }),
          }}
        >
          {main}
        </div>
      </div>
      {footer ? (
        <div
          css={{
            ...(isChildOverflow && {
              borderTopLeftRadius: "24px",
              borderTopRightRadius: "24px",
            }),
            ...(isChildOverflow &&
              cssFns.boxShadow({
                offsetY: "-4px",
                blurRadius: "20px",
                color: cssFns.setOpacity(uiColors.everBack, 0.12),
              })),
            backgroundColor: isChildOverflow
              ? uiColors.backgroundFloating
              : uiColors.background,
          }}
        >
          {footer}
        </div>
      ) : undefined}
    </div>
  );
};
